import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import ModuleGate from "@/components/base/ModuleGate";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logActivity } from "@/lib/logActivity";
import { createNotification } from "@/lib/createNotification";
import { createReminder, hoursBefore } from "@/lib/createReminder";
import { queueEmail, appointmentReminderBody } from "@/lib/queueEmail";
import AppointmentModal, {
  type Appointment,
  type ClientOption,
  type LeadOption,
  type ContractOption,
  type JobOption,
} from "./components/AppointmentModal";
import AppointmentsListView     from "./components/AppointmentsListView";
import AppointmentsCalendarView from "./components/AppointmentsCalendarView";
import AppointmentsSettingsTab  from "./components/AppointmentsSettingsTab";

/* ─── View tabs ───────────────────────────────────────────────── */
type ViewTab = "list" | "calendar" | "settings";

const VIEW_TABS: { value: ViewTab; label: string; icon: string }[] = [
  { value: "list",     label: "List",     icon: "ri-list-unordered" },
  { value: "calendar", label: "Calendar", icon: "ri-calendar-2-line" },
  { value: "settings", label: "Settings", icon: "ri-settings-3-line" },
];

const fmtDateShort = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} at ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
};

const hoursBeforeDate = (iso: string, hours: number) => {
  const d = new Date(iso);
  d.setHours(d.getHours() - hours);
  return d.toISOString();
};

/* ─── Page ────────────────────────────────────────────────────── */
export default function AdminAppointmentsPage() {
  const navigate = useNavigate();

  const [tenantId, setTenantId]   = useState<string | null>(null);
  const [appts, setAppts]         = useState<Appointment[]>([]);
  const [clients, setClients]     = useState<ClientOption[]>([]);
  const [leads, setLeads]         = useState<LeadOption[]>([]);
  const [contracts, setContracts] = useState<ContractOption[]>([]);
  const [jobs, setJobs]           = useState<JobOption[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  /* view tab */
  const [viewTab, setViewTab] = useState<ViewTab>("list");

  /* modal */
  const [modalOpen, setModalOpen]       = useState(false);
  const [selected, setSelected]         = useState<Appointment | null>(null);
  const [isNew, setIsNew]               = useState(false);
  const [prefillStartAt, setPrefill]    = useState<string | undefined>();

  /* ── Fetch ── */
  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [apptRes, clientRes, leadRes, contractRes, jobRes] = await Promise.all([
        supabase.from("appointments").select("*").eq("tenant_id", tid).order("start_at", { ascending: true }),
        supabase.from("clients").select("id, full_name, email").eq("tenant_id", tid).eq("status", "active").order("full_name"),
        supabase.from("tenant_public_leads").select("id, name").eq("tenant_id", tid).order("name"),
        supabase.from("contracts").select("id, title, contract_number").eq("tenant_id", tid).order("title"),
        supabase.from("jobs").select("id, title").eq("tenant_id", tid).order("title"),
      ]);
      if (apptRes.error) throw new Error(apptRes.error.message);
      setAppts((apptRes.data ?? []) as Appointment[]);
      setClients((clientRes.data ?? []) as ClientOption[]);
      setLeads((leadRes.data ?? []) as LeadOption[]);
      setContracts((contractRes.data ?? []) as ContractOption[]);
      setJobs((jobRes.data ?? []) as JobOption[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load appointments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  /* ── Stats ── */
  const upcomingCount = useMemo(() =>
    appts.filter((a) => (a.status === "scheduled" || a.status === "confirmed") && new Date(a.start_at) >= new Date()).length,
  [appts]);

  const todayCount = useMemo(() =>
    appts.filter((a) => new Date(a.start_at).toDateString() === new Date().toDateString()).length,
  [appts]);

  const completedCount = useMemo(() => appts.filter((a) => a.status === "completed").length, [appts]);

  /* ── Unique appointment types ── */
  const apptTypes = useMemo(() => {
    const s = new Set(appts.map((a) => a.appointment_type).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [appts]);

  /* ── Modal handlers ── */
  const handleNew = () => {
    setSelected(null);
    setIsNew(true);
    setPrefill(undefined);
    setModalOpen(true);
  };

  const handleNewFromDay = (isoDate: string) => {
    setSelected(null);
    setIsNew(true);
    setPrefill(`${isoDate}T09:00`);
    setModalOpen(true);
  };

  const handleEdit = (appt: Appointment) => {
    setSelected(appt);
    setIsNew(false);
    setPrefill(undefined);
    setModalOpen(true);
  };

  /* ── Save (create / update) ── */
  const handleSave = async (data: Appointment) => {
    if (!tenantId) return;

    if (isNew) {
      const { id: _id, tenant_id: _t, created_at: _ca, updated_at: _ua, ...rest } = data;
      const { data: created, error } = await supabase
        .from("appointments")
        .insert({ ...rest, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (created) {
        const createdAppt = created as Appointment;
        setAppts((prev) =>
          [...prev, createdAppt].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
        );
        logActivity({ tenantId, action: "created", entity: "appointment", entityId: createdAppt.id, entityName: data.title });
        createNotification({ tenantId, type: "info", category: "appointment", title: "Appointment created", message: `"${data.title}" scheduled for ${fmtDateShort(data.start_at)}.`, entityType: "appointment", entityId: createdAppt.id });
        // Queue reminder 24h before start — fire-and-forget
        createReminder({
          tenantId,
          reminderType: "custom",
          entityType:   "appointment",
          entityId:     createdAppt.id,
          reminderDate: hoursBefore(data.start_at, 24),
          message:      "Appointment reminder",
        });

        // ── Queue appointment reminder email ──
        const clientRecord = clients.find((c) => c.id === data.client_id);
        if (clientRecord?.email) {
          const startDate = new Date(data.start_at);
          const isToday = new Date().toDateString() === startDate.toDateString();
          const reminderHours = isToday ? 2 : 24;
          queueEmail({
            tenantId,
            toEmail: clientRecord.email,
            subject: `Reminder: ${data.title} — ${fmtDateShort(data.start_at)}`,
            body: appointmentReminderBody({
              clientName: clientRecord.full_name,
              appointmentTitle: data.title,
              appointmentDate: fmtDateShort(data.start_at),
              appointmentTime: startDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
              location: data.location || undefined,
              studioName: "Our Studio", // placeholder — will be overwritten by edge function using branding
            }),
            templateType: "appointment_reminder",
            entityType: "appointment",
            entityId: createdAppt.id,
            priority: 2,
            scheduledFor: hoursBeforeDate(data.start_at, reminderHours),
            metadata: {
              appointment_title: data.title,
              start_at: data.start_at,
              location: data.location,
              client_name: clientRecord.full_name,
              reminder_hours: reminderHours,
            },
          });
        }
      }
      Swal.fire({ title: "Created!", text: "Appointment added.", icon: "success", timer: 1500, showConfirmButton: false });
    } else {
      const { data: updated, error } = await supabase
        .from("appointments")
        .update({
          title:            data.title,
          appointment_type: data.appointment_type,
          status:           data.status,
          start_at:         data.start_at,
          end_at:           data.end_at,
          location:         data.location,
          notes:            data.notes,
          show_in_portal:   data.show_in_portal,
          client_id:        data.client_id,
          lead_id:          data.lead_id,
          contract_id:      data.contract_id,
          job_id:           data.job_id,
          updated_at:       new Date().toISOString(),
        })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
      if (updated) {
        const updatedAppt = updated as Appointment;
        setAppts((prev) => prev.map((a) => a.id === updatedAppt.id ? updatedAppt : a));
        logActivity({ tenantId, action: "updated", entity: "appointment", entityId: data.id, entityName: data.title });
        // If start_at changed, update the existing unsent reminder
        const startChanged = selected?.start_at !== data.start_at;
        if (startChanged) {
          const { data: existingRem } = await supabase
            .from("reminders")
            .select("id")
            .eq("tenant_id", tenantId)
            .eq("entity_type", "appointment")
            .eq("entity_id", data.id)
            .eq("is_sent", false)
            .maybeSingle();
          if (existingRem) {
            await supabase
              .from("reminders")
              .update({ reminder_date: hoursBefore(data.start_at, 24) })
              .eq("id", existingRem.id);
          } else {
            createReminder({
              tenantId,
              reminderType: "custom",
              entityType:   "appointment",
              entityId:     data.id,
              reminderDate: hoursBefore(data.start_at, 24),
              message:      "Appointment reminder",
            });
          }

          // ── Update / re-queue appointment reminder email if start changed ──
          const clientRecord = clients.find((c) => c.id === data.client_id);
          if (clientRecord?.email) {
            const startDate = new Date(data.start_at);
            const isToday = new Date().toDateString() === startDate.toDateString();
            const reminderHours = isToday ? 2 : 24;
            // Mark previous unsent emails for this appointment as superseded
            await supabase
              .from("email_queue")
              .update({ status: "failed", last_error: "Superseded by schedule change" })
              .eq("tenant_id", tenantId)
              .eq("entity_type", "appointment")
              .eq("entity_id", data.id)
              .eq("status", "pending");
            queueEmail({
              tenantId,
              toEmail: clientRecord.email,
              subject: `Updated Reminder: ${data.title} — ${fmtDateShort(data.start_at)}`,
              body: appointmentReminderBody({
                clientName: clientRecord.full_name,
                appointmentTitle: data.title,
                appointmentDate: fmtDateShort(data.start_at),
                appointmentTime: startDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
                location: data.location || undefined,
                studioName: "Our Studio",
              }),
              templateType: "appointment_reminder",
              entityType: "appointment",
              entityId: data.id,
              priority: 2,
              scheduledFor: hoursBeforeDate(data.start_at, reminderHours),
              metadata: {
                appointment_title: data.title,
                start_at: data.start_at,
                location: data.location,
                client_name: clientRecord.full_name,
                reminder_hours: reminderHours,
                is_rescheduled: true,
              },
            });
          }
        }
      }
      Swal.fire({ title: "Saved!", text: "Appointment updated.", icon: "success", timer: 1400, showConfirmButton: false });
    }
    setModalOpen(false);
  };

  /* ── Quick status ── */
  const handleQuickStatus = async (appt: Appointment, next: string) => {
    if (!tenantId) return;
    const { error } = await supabase
      .from("appointments")
      .update({ status: next, updated_at: new Date().toISOString() })
      .eq("id", appt.id)
      .eq("tenant_id", tenantId);
    if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
    setAppts((prev) => prev.map((a) => a.id === appt.id ? { ...a, status: next } : a));
    if (next === "confirmed") createNotification({ tenantId, type: "success", title: "Appointment confirmed", message: `"${appt.title}" confirmed.` });
    if (next === "completed") createNotification({ tenantId, type: "info",    category: "appointment", title: "Appointment completed", message: `"${appt.title}" completed.`, entityType: "appointment", entityId: appt.id });
  };

  /* ── Delete ── */
  const handleDelete = async (appt: Appointment) => {
    if (!tenantId) return;
    const result = await Swal.fire({
      title: "Delete appointment?",
      text: `"${appt.title}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;
    const { error } = await supabase.from("appointments").delete().eq("id", appt.id).eq("tenant_id", tenantId);
    if (error) { Swal.fire({ title: "Error", text: error.message, icon: "error" }); return; }
    setAppts((prev) => prev.filter((a) => a.id !== appt.id));
    logActivity({ tenantId, action: "deleted", entity: "appointment", entityId: appt.id, entityName: appt.title });
    // Cancel pending emails for this appointment
    await supabase
      .from("email_queue")
      .update({ status: "failed", last_error: "Appointment cancelled" })
      .eq("tenant_id", tenantId)
      .eq("entity_type", "appointment")
      .eq("entity_id", appt.id)
      .eq("status", "pending");
  };

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Appointments"
        subtitle="Schedule and manage client appointments"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8975e] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
            Create Appointment
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">
        <ModuleGate moduleId="appointments">

          {/* ── Stats row ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total",     value: appts.length,   icon: "ri-calendar-2-line",    color: "text-slate-500",   bg: "bg-slate-50" },
              { label: "Today",     value: todayCount,     icon: "ri-calendar-todo-line", color: "text-[#C8A96E]",   bg: "bg-amber-50" },
              { label: "Upcoming",  value: upcomingCount,  icon: "ri-time-line",          color: "text-sky-500",     bg: "bg-sky-50" },
              { label: "Completed", value: completedCount, icon: "ri-check-double-line",  color: "text-emerald-500", bg: "bg-emerald-50" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl border border-slate-200 px-5 py-4">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.bg} ${s.color} mb-2`}>
                  <i className={`${s.icon} text-sm`}></i>
                </div>
                <p className="text-2xl font-bold text-[#0f172a]">{s.value}</p>
                <p className="text-slate-400 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── View tabs ── */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-6 w-fit">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setViewTab(tab.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  viewTab === tab.value
                    ? "bg-[#C8A96E] text-white"
                    : "text-slate-400 hover:text-[#0f172a]"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Content ── */}
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
          ) : (
            <>
              {viewTab === "list" && (
                <AppointmentsListView
                  appointments={appts}
                  clients={clients}
                  loading={false}
                  apptTypes={apptTypes}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onQuickStatus={handleQuickStatus}
                  onNew={handleNew}
                />
              )}
              {viewTab === "calendar" && (
                <AppointmentsCalendarView
                  appointments={appts}
                  clients={clients}
                  onClickAppt={handleEdit}
                  onClickDay={handleNewFromDay}
                />
              )}
              {viewTab === "settings" && (
                <AppointmentsSettingsTab />
              )}
            </>
          )}

        </ModuleGate>
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <AppointmentModal
          appointment={selected}
          isNew={isNew}
          clients={clients}
          leads={leads}
          contracts={contracts}
          jobs={jobs}
          prefillStartAt={prefillStartAt}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </AdminLayout>
  );
}